import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Layers,
  Sparkles,
  Handshake,
  ShieldCheck,
  Check,
} from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { BUSINESS } from "@/lib/content";

const PILLARS = [
  {
    icon: Building2,
    title: "Commercial Cleaning Specialists",
    body: "Commercial cleaning isn't simply one of our services — it's what we do best. Our systems, equipment and quality assurance processes are built specifically to meet the demands of commercial environments.",
    points: [
      "Purpose-built processes for commercial-grade sites",
      "Equipment and consumables selected for volume and consistency",
      "Written scope, checklist and sign-off for every job",
    ],
  },
  {
    icon: Layers,
    title: "Scalable Solutions",
    body: "Whether it's a small office, a retail store, a medical facility, a builders' final clean or a large commercial contract, we have the experience and capability to deliver projects of every size.",
    points: [
      "One-off projects and ongoing multi-site contracts",
      "Weekday, out-of-hours and weekend scheduling",
      "Capacity to scale up for large developments and handovers",
    ],
  },
  {
    icon: Sparkles,
    title: "Quality Without Compromise",
    body: "Every project is completed with meticulous attention to detail, professional accountability and a commitment to exceeding expectations.",
    points: [
      "Detail-driven finish, inspected before we leave site",
      "Consistent standard across every visit and every property",
      "Responsive follow-up if anything ever needs revisiting",
    ],
  },
  {
    icon: Handshake,
    title: "Reliable Long-Term Partnerships",
    body: "We believe the best business relationships are built on trust, communication and consistency. Our objective is to become the preferred commercial cleaning provider for businesses, builders, developers and property managers throughout South East Queensland.",
    points: [
      "Clear, direct point of contact — no call centres",
      "Straightforward pricing and predictable invoicing",
      "Structured to grow with your business over time",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Safety & Professionalism",
    body: "Our team operates with professionalism, respect and a strong commitment to workplace safety, ensuring every project is completed efficiently and to the highest standard.",
    points: [
      "Site-appropriate safety awareness and site inductions",
      "Respectful presence on active sites and occupied premises",
      "Professional presentation on every visit",
    ],
  },
];

export default function WhyUs() {
  return (
    <>
      <SEO
        title="Why Choose Savage Cleaning — Commercial Cleaning Provider in SEQ"
        description="Five reasons businesses, builders and property managers across Brisbane, the Gold Coast and South East Queensland choose Savage Cleaning as their commercial cleaning partner."
      />

      {/* HERO */}
      <section className="relative py-24 sm:py-32 border-b border-white/5" data-testid="whyus-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="gold-rule" />
              <span className="eyebrow">Why Savage Cleaning</span>
            </div>
            <h1 className="mt-6 font-serif-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05]">
              Built for commercial. Trusted long-term.
            </h1>
            <p className="mt-8 text-lg text-white/85 leading-relaxed max-w-2xl">
              Five reasons businesses, builders, developers and property managers across Brisbane,
              the Gold Coast and South East Queensland partner with Savage Cleaning.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 sm:py-24" data-testid="whyus-pillars">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-16 sm:space-y-24">
          {PILLARS.map(({ icon: Icon, title, body, points }, idx) => (
            <div
              key={title}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start"
              data-testid={`whyus-pillar-${idx}`}
            >
              <div className="lg:col-span-5">
                <div className="eyebrow">Reason 0{idx + 1}</div>
                <div className="mt-4 flex items-start gap-4">
                  <Icon size={30} strokeWidth={1.4} className="text-[#C9A24B] mt-2 flex-shrink-0" />
                  <h2 className="font-serif-display text-3xl sm:text-4xl text-white leading-tight">
                    {title}
                  </h2>
                </div>
              </div>
              <div className="lg:col-span-7">
                <p className="text-white/85 text-lg leading-relaxed">{body}</p>
                <ul className="mt-6 space-y-3">
                  {points.map((p) => (
                    <li key={p} className="flex gap-3 text-white/80">
                      <Check size={18} className="text-[#C9A24B] mt-1 flex-shrink-0" strokeWidth={1.8} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TAGLINE */}
      <section className="py-20 sm:py-28 bg-[#111114] border-y border-white/5" data-testid="whyus-tagline">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="border-l-2 border-[#C9A24B] pl-6 sm:pl-8">
            <p className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-white leading-snug">
              "{BUSINESS.tagline}"
            </p>
          </div>
          <div className="mt-10">
            <Link to="/contact" className="btn-primary" data-testid="whyus-cta-quote">
              Get a Free Quote <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to work with a commercial cleaner you can rely on?"
        subtitle="Tell us about your site, contract or project and we'll come back with a clear scope and honest quote."
      />
    </>
  );
}
